import {
    faCheckCircle,
    faClock,
    faDownload,
    faFileExcel
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadIndicator } from 'devextreme-react';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import 'moment/locale/tr';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import Breadcrumb from '../../components/common/breadcrumb';
import api from '../../services/api-configuration';
import { ButtonOnExporting } from '../../utils/DataGridExporter';
import './SevkiyatTakibi.css';

const SevkiyatTakibi = () => {
    const [summaryData, setSummaryData] = useState([]);
    const [detailedData, setDetailedData] = useState([]);
    const [pendingVehiclesCount, setPendingVehiclesCount] = useState(0);
    const [pendingGroupages, setPendingGroupages] = useState([]);
    const [isLoadingCount, setIsLoadingCount] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const summaryDataGridRef = useRef(null);
    const detailedDataGridRef = useRef(null);
    const pendingDataGridRef = useRef(null);
    const pendingVehiclesRef = useRef(null);

    useEffect(() => {
        fetchSummaryData();
        fetchDetailedData();
        fetchPendingVehiclesData();
    }, []);

    const fetchSummaryData = async () => {
        if (summaryDataGridRef.current) {
            summaryDataGridRef.current.instance.beginCustomLoading();
        }
        try {
            const response = await api.post('/getDoorExitTotalForLastTenDays');
            if (response.data) {
                const formattedData = response.data.map(item => ({
                    date: new Date(item.door_exit_date).toLocaleDateString('tr-TR'),
                    totalVehicles: item.door_exit_vehicle_count,
                    delivered: item.delivery_vehicle_count,
                    deliveryRate: item.delivery_rate.toFixed(2) + '%'
                }));
                setSummaryData(formattedData);
            }
        } catch (error) {
            console.error('Error fetching summary data:', error);
            toast.error('Sevkiyat özeti yüklenirken hata oluştu');
        } finally {
            if (summaryDataGridRef.current) {
                summaryDataGridRef.current.instance.endCustomLoading();
            }
        }
    };

    const fetchDetailedData = async () => {
        if (detailedDataGridRef.current) {
            detailedDataGridRef.current.instance.beginCustomLoading();
        }
        try {
            const response = await api.post('/getDoorExitGroupages', {
                start_date: '1900-01-01',
                end_date: '9999-12-31'
            });
            if (response.data) {
                const sortedData = response.data
                    .sort((a, b) => new Date(b.groupage_create_date) - new Date(a.groupage_create_date));
                setDetailedData(sortedData);
            }
        } catch (error) {
            console.error('Error fetching detailed data:', error);
            toast.error('Detaylı sevkiyat bilgileri yüklenirken hata oluştu');
        } finally {
            if (detailedDataGridRef.current) {
                detailedDataGridRef.current.instance.endCustomLoading();
            }
        }
    };

    const fetchPendingVehiclesData = async () => {
        setIsLoadingCount(true);
        if (pendingVehiclesRef.current) {
            pendingVehiclesRef.current.style.opacity = '0.5';
        }
        try {
            const response = await api.post('/getPendingGroupagesAfterDoorExit');
            if (response.data) {
                setPendingGroupages(response.data);
                const totalPendingVehicles = response.data.reduce((sum, item) => sum + item.groupage_vehicle_count, 0);
                setPendingVehiclesCount(totalPendingVehicles);
            }
        } catch (error) {
            console.error('Error fetching pending vehicles data:', error);
            toast.error('Bekleyen araç bilgileri yüklenirken hata oluştu');
        } finally {
            if (pendingVehiclesRef.current) {
                pendingVehiclesRef.current.style.opacity = '1';
            }
            setIsLoadingCount(false);
        }
    };

    const formatNumber = (number) => {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    const downloadExcel = async () => {
        setIsDownloading(true);
        try {
            if (!pendingGroupages || !Array.isArray(pendingGroupages)) {
                console.error('İndirilebilir veri bulunamadı');
                return;
            }

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Bekleyen Araçlar');

            // Başlıkları tanımla
            const headers = [
                'Grupaj No',
                'Nereden',
                'Nereye',
                'GrupajdakiAraç Adeti',
                'Grupaj Tarihi',
                'Kapı Çıkış Tarihi',
                'Taşıyıcı Firma',
                'Nakliyeci Teslim Bilgisi'
            ];

            // Başlık satırını ekle
            const headerRow = worksheet.addRow(headers);

            // Filtre ekle
            worksheet.autoFilter = {
                from: { row: 1, column: 1 },
                to: { row: 1, column: headers.length }
            };

            // Başlık stilini uygula
            headerRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '4472C4' }
                };
                cell.font = { color: { argb: 'FFFFFF' }, bold: true };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'D9D9D9' } },
                    left: { style: 'thin', color: { argb: 'D9D9D9' } },
                    bottom: { style: 'thin', color: { argb: 'D9D9D9' } },
                    right: { style: 'thin', color: { argb: 'D9D9D9' } }
                };
            });

            // Veri satırlarını ekle
            pendingGroupages.forEach((item, index) => {
                const row = [
                    item.groupage_no,
                    item.from_location,
                    item.to_location,
                    item.groupage_vehicle_count,
                    moment(item.groupage_create_date).format('DD.MM.YYYY'),
                    moment(item.door_exit_date).format('DD.MM.YYYY'),
                    item.contractor_name,
                    item.delivery_status
                ];

                const excelRow = worksheet.addRow(row);
                const isEvenRow = index % 2 === 0;

                // Her hücreye stil uygula
                excelRow.eachCell({ includeEmpty: true }, (cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: isEvenRow ? 'F2F2F2' : 'FFFFFF' }
                    };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'D9D9D9' } },
                        left: { style: 'thin', color: { argb: 'D9D9D9' } },
                        bottom: { style: 'thin', color: { argb: 'D9D9D9' } },
                        right: { style: 'thin', color: { argb: 'D9D9D9' } }
                    };
                });
            });

            // Sütun genişliklerini otomatik ayarla
            worksheet.columns.forEach((column, index) => {
                const maxLength = Math.max(
                    ...worksheet.getColumn(index + 1).values
                        .filter(value => value)
                        .map(value => value.toString().length)
                );
                column.width = Math.min(Math.max(maxLength + 2, 15), 30);
            });

            // Excel dosyasını kaydet
            const buffer = await workbook.xlsx.writeBuffer();
            const fileName = `Bekleyen_Araclar_${moment().format('YYYY-MM-DD HH:mm:ss')}.xlsx`;
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);

        } catch (error) {
            console.error('Excel indirme hatası:', error);
            toast.error('Excel dosyası oluşturulurken bir hata oluştu');
        } finally {
            setIsDownloading(false);
        }
    };

    const getStatusBadge = (status) => {
        if (status === 'Teslim Edildi') {
            return (
                <span
                    style={{
                        padding: '4px 8px',
                        backgroundColor: '#f5f5f5',
                        color: '#2c3e50',
                        borderRadius: '2px',
                        fontSize: '13px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {status}
                </span>
            );
        }
        return (
            <span
                style={{
                    padding: '4px 8px',
                    backgroundColor: status.includes('(') ? 'rgb(255 185 0)' : '#f5f5f5',
                    color: '#2c3e50',
                    borderRadius: '2px',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: '1px solid #e0e0e0'
                }}
            >
                <FontAwesomeIcon icon={faClock} />
                {status}
            </span>
        );
    };

    return (
        <div className="sevkiyat-takibi-container">
            <Breadcrumb current="Sevkiyat Takibi" />

            <Row className="mb-2 g-3">
                <Col xs={12} lg={8}>
                    <Card className="h-100">
                        <CardBody className="p-2">
                            <DataGrid
                                ref={summaryDataGridRef}
                                dataSource={summaryData}
                                showBorders={true}
                                rowAlternationEnabled={true}
                                hoverStateEnabled={true}
                                height="100%"
                                loadingIndicator={{ text: 'Yükleniyor...' }}
                            >
                                <Column dataField="date" caption="Tarih" minWidth={"100"} />
                                <Column dataField="totalVehicles" caption="Toplam Araç" />
                                <Column dataField="delivered" caption="Teslim Edilen" />
                                <Column
                                    dataField="deliveryRate"
                                    caption="Teslim Oranı"
                                    width={120}
                                    cellRender={(data) => (
                                        <span className="delivery-rate">
                                            {data.value}
                                        </span>
                                    )}
                                />
                            </DataGrid>
                        </CardBody>
                    </Card>
                </Col>
                <Col xs={12} lg={4}>
                    <Card className="h-100">
                        <CardBody className="d-flex flex-column justify-content-center p-3">
                            <div className="text-center">
                                <p className="pending-vehicles-title">
                                    Kapı Çıkış tarihi 36 saati geçen ve teslim bilgisi gelmeyen
                                </p>
                                <h2
                                    ref={pendingVehiclesRef}
                                    className="pending-vehicles-count"
                                >
                                    {isLoadingCount ? (
                                        <LoadIndicator height={50} width={50} />
                                    ) : (
                                        <>
                                            {formatNumber(pendingVehiclesCount)}
                                            <span className="pending-vehicles-unit">araç</span>
                                        </>
                                    )}
                                </h2>

                                <button
                                    onClick={downloadExcel}
                                    disabled={isDownloading || !pendingGroupages?.length}
                                    className="download-button"
                                >
                                    {isDownloading ? (
                                        <>
                                            <LoadIndicator height={16} width={16} />
                                            İndiriliyor...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faDownload} />
                                            İndir
                                        </>
                                    )}
                                </button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Card className="detailed-card mt-3">
                <CardHeader className="d-flex align-items-center justify-content-between p-2 px-4">
                    <div className="d-flex align-items-center">
                    </div>
                    <button
                        className="export-button"
                        onClick={(e) => ButtonOnExporting(e, detailedDataGridRef, 'Sevkiyat_Takibi_Detayli_Sevkiyat')}
                    >
                        <FontAwesomeIcon icon={faFileExcel} />
                        <span>Excel'e Aktar</span>
                    </button>
                </CardHeader>
                <CardBody className="p-2">
                    <DataGrid
                        ref={detailedDataGridRef}
                        dataSource={detailedData}
                        showBorders={true}
                        height="500"
                        rowAlternationEnabled={true}
                        hoverStateEnabled={true}
                        allowColumnResizing={true}
                        filterRow={{ visible: true }}
                        loadingIndicator={{ text: 'Yükleniyor...' }}
                    >
                        <Column dataField="groupage_no" caption="Grupaj No" width={150} />
                        <Column dataField="from_location" caption="Nereden" minWidth={180} />
                        <Column dataField="to_location" caption="Nereye" minWidth={150} />
                        <Column dataField="groupage_vehicle_count" caption="Araç Adeti" width={110} alignment='center' />
                        <Column dataField="groupage_create_date" format={"dd.MM.yyyy"} caption="Grupaj Tarihi" width={120} dataType="date" />
                        <Column dataField="door_exit_date" caption="Kapı Çıkış" width={120} dataType="date" format={"dd.MM.yyyy"} />
                        <Column dataField="contractor_name" caption="Taşıyıcı Firma" width={150} />
                        <Column
                            dataField="delivery_status"
                            caption="Teslim Durumu"
                            width={165}
                            cellRender={(data) => getStatusBadge(data.value)}
                        />
                    </DataGrid>
                </CardBody>
            </Card>

            <style jsx>{`
                .sevkiyat-takibi-container {
                    padding: 15px;
                }

                .delivery-rate {
                    font-weight: 700;
                    color: #2c3e50;
                }

                .pending-vehicles-title {
                    color: #34495e;
                    margin-bottom: 20px;
                    font-size: 19px;
                    line-height: 1.6;
                    font-weight: 400;
                }

                .pending-vehicles-count {
                    font-size: 48px;
                    font-weight: 700;
                    color: rgb(238 0 0);
                    margin-bottom: 19px;
                    line-height: 1.2;
                    display: flex;
                    align-items: baseline;
                    gap: 8px;
                    transition: opacity 0.3s ease;
                    justify-content: center;
                }

                .pending-vehicles-unit {
                    color: #34495e;
                    font-size: 16px;
                    line-height: 1.6;
                    max-width: 280px;
                    font-weight: 400;
                }

                .download-button {
                    padding: 8px 16px;
                    font-size: 14px;
                    font-weight: 500;
                    color: #2c3e50;
                    background-color: #f5f5f5;
                    border: 1px solid #e0e0e0;
                    border-radius: 2px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    width: 100%;
                    max-width: 280px;
                    justify-content: center;
                    margin: 0 auto;
                }

                .download-button:disabled {
                    cursor: not-allowed;
                    opacity: 0.5;
                }

                .export-button {
                    padding: 6px 12px;
                    font-size: 14px;
                    font-weight: 500;
                    color: #2c3e50;
                    background-color: #f5f5f5;
                    border: 1px solid #e0e0e0;
                    border-radius: 2px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .export-button svg {
                    font-size: 16px;
                    color: #484848;
                }

                @media (max-width: 768px) {
                    .pending-vehicles-count {
                        font-size: 36px;
                    }

                    .pending-vehicles-title {
                        font-size: 16px;
                    }

                    .download-button {
                        max-width: 100%;
                    }
                }

                @media (max-width: 576px) {
                    .sevkiyat-takibi-container {
                        padding: 10px;
                    }

                    .pending-vehicles-count {
                        font-size: 32px;
                    }

                    .pending-vehicles-title {
                        font-size: 14px;
                    }
                }
            `}</style>
        </div>
    );
};

export default SevkiyatTakibi; 